import { Button, FormDialog, Input } from "@simplicity/ui";
import { useState } from "react";

const COPY = {
  create: {
    title: "Create User Group",
    description: "Enter a name for the new group.",
    cta: "Create",
    pending: "Creating...",
  },
  rename: {
    title: "Rename User Group",
    description: "Members and dashboard assignments are unaffected by a rename.",
    cta: "Save",
    pending: "Saving...",
  },
} as const;

/**
 * Name-a-group dialog, shared by the create and rename flows (SIM-2553). `initialName` seeds the
 * field via `useState`, so the caller must remount this component when the target changes — pass a
 * `key` derived from the mode + group id. That keeps the seeding effect-free (no `useEffect` sync).
 */
export function GroupDialog({
  open,
  mode,
  initialName = "",
  onOpenChange,
  onSubmit,
  isPending,
}: {
  open: boolean;
  mode: "create" | "rename";
  initialName?: string;
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string) => void;
  isPending: boolean;
}) {
  const [name, setName] = useState(initialName);
  const copy = COPY[mode];

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
    if (mode === "create") setName("");
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={copy.title}
      description={copy.description}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending || !name.trim()}>
            {isPending ? copy.pending : copy.cta}
          </Button>
        </>
      }
    >
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
          }
        }}
        placeholder="Group name..."
        aria-label="Group name"
        autoFocus
      />
    </FormDialog>
  );
}
